import {
  ArrowLeft,
  Button,
  Edit,
  Globe,
  Info,
  Mail,
  MapPin,
  Phone,
  Star,
  Tag,
  ToggleLeft,
  ToggleRight,
  User,
  UserPlus,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { LoadingSpinner } from "../components/atoms/LoadingSpinner/LoadingSpinner";
import { AssignManagerModal } from "../components/molecules/AssignManagerModal/AssignManagerModal";
import { ConfirmationModal } from "../components/molecules/ConfirmationModal/ConfirmationModal";
import { EditFacilityModal } from "../components/molecules/EditFacilityModal/EditFacilityModal";
import { Modal } from "../components/molecules/Modal/Modal";
import { StatusBadge } from "../components/molecules/StatusBadge/StatusBadge";
import { facilitiesApi } from "../services/facilities.api";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  clearError,
  fetchFacilityByIdRequest,
  selectFacilities,
  selectFacilitiesError,
  selectFacilitiesLoading,
  selectSelectedFacility,
  setSelectedFacility,
} from "../store/reducers/facilitySlice";
import { useSpecialtyLabels } from "../hooks/useSpecialtyLabels";
import { FacilityType } from "../types/facility.definition";
import { Manager } from "../types/manager.definition";
import {
  civilityLabel,
  facilityTypeLabel,
  weekdayLabel,
} from "../utils/enum-label-utils";
import { FacilityLicenceSection } from "./components/FacilityLicenceSection";

interface FacilityDetailPageProps {
  facilityId?: string;
}

export const FacilityDetailPage: React.FC<FacilityDetailPageProps> = ({
  facilityId: propFacilityId,
}) => {
  const { t } = useTranslation();
  // MED-1462 / MED-1523: the specialty labels embedded in the facility payload
  // are not localized; resolve them against the language-aware store.
  const resolveSpecialtyLabel = useSpecialtyLabels();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { facilityId: paramFacilityId } = useParams<{ facilityId: string }>();

  // Redux state
  const facilities = useAppSelector(selectFacilities);
  const selectedFacility = useAppSelector(selectSelectedFacility);
  const loading = useAppSelector(selectFacilitiesLoading);
  const error = useAppSelector(selectFacilitiesError);

  // Local state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showFeaturesModal, setShowFeaturesModal] = useState(false);
  const [showToggleStatusModal, setShowToggleStatusModal] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);

  // Use prop facilityId if provided, otherwise use URL param
  const facilityId = propFacilityId || paramFacilityId;

  // The manager of THIS facility, asked for by facility id (MED-1422).
  // The whole-list endpoint this page used to scan is paged at 20 rows server side,
  // so any manager outside that window looked like "no manager assigned".
  const [facilityManager, setFacilityManager] = useState<Manager | null>(null);

  // Get facility from cache or fetch if needed
  const facility =
    selectedFacility || facilities.find(f => String(f.id) === facilityId);

  // Fetch facility data if not in cache
  useEffect(() => {
    if (!facilityId) {
      return;
    }

    // Check if we have this facility in the facilities list
    const cachedFacility = facilities.find(f => String(f.id) === facilityId);

    if (cachedFacility) {
      dispatch(setSelectedFacility(cachedFacility));
      return;
    }

    // Only fetch if we're not already loading
    dispatch(fetchFacilityByIdRequest(facilityId));
  }, [facilityId, dispatch, facilities]);

  // Fetch the manager of this facility. This endpoint filters on disabled only, so a
  // manager who has not yet changed their temporary password is still returned.
  const loadFacilityManager = useCallback(() => {
    if (!facilityId) {
      return;
    }
    let cancelled = false;
    facilitiesApi
      .getFacilityManagers(Number(facilityId))
      .then(list => {
        if (!cancelled) {
          setFacilityManager(list[0] ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFacilityManager(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [facilityId]);

  useEffect(() => loadFacilityManager(), [loadFacilityManager]);

  // Clear error and selected facility when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  const handleToggleStatus = () => {
    setShowToggleStatusModal(true);
  };

  // MED-1443: this used to flip `disabled` in Redux only — the badge changed
  // instantly and a reload put it straight back. Call the API and only reflect
  // the new status once the server has accepted it.
  const handleToggleStatusConfirm = async () => {
    if (!facility || togglingStatus) {
      return;
    }
    const wasDisabled = !!facility.disabled;
    setTogglingStatus(true);
    try {
      if (wasDisabled) {
        await facilitiesApi.restoreFacility(Number(facility.id));
      } else {
        await facilitiesApi.disableFacility(Number(facility.id));
      }
      dispatch(setSelectedFacility({ ...facility, disabled: !wasDisabled }));
      setShowToggleStatusModal(false);
      toast.success(
        wasDisabled
          ? t("admin.facilities.toggleStatus.enableSuccess")
          : t("admin.facilities.toggleStatus.disableSuccess")
      );
    } catch {
      // Leave the badge untouched — the facility is still in its old state.
      toast.error(
        wasDisabled
          ? t("admin.facilities.toggleStatus.enableError")
          : t("admin.facilities.toggleStatus.disableError")
      );
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleToggleStatusCancel = () => {
    if (togglingStatus) {
      return;
    }
    setShowToggleStatusModal(false);
  };

  const handleAssignManagerSuccess = (_manager: Manager) => {
    setShowAssignModal(false);
    loadFacilityManager();
    if (facilityId) {
      dispatch(fetchFacilityByIdRequest(facilityId));
    }
  };

  const handleEditFacilitySuccess = () => {
    setShowEditModal(false);
    if (facilityId) {
      dispatch(fetchFacilityByIdRequest(facilityId));
    }
  };

  const getFacilityTypeColor = (type: FacilityType) => {
    switch (type) {
      case FacilityType.HOSPITAL:
        return "bg-danger-100 text-danger-800";
      case FacilityType.CLINIC:
        return "bg-primary-100 text-primary-800";
      case FacilityType.NURSING_HOME:
        return "bg-secondary-100 text-secondary-800";
      case FacilityType.RADIOLOGY_CENTER:
        return "bg-warning-100 text-warning-800";
      case FacilityType.HEALTH_CENTRE:
        return "bg-success-100 text-success-800";
      case FacilityType.EMERGENCY_SERVICES:
        return "bg-danger-100 text-danger-800";
      case FacilityType.PHYSIOTHERAPY_CENTER:
        return "bg-primary-100 text-primary-800";
      case FacilityType.HEALTH_POST:
        return "bg-neutral-100 text-neutral-800";
      default:
        return "bg-neutral-100 text-neutral-800";
    }
  };

  const getFacilityTypeIcon = (type: FacilityType) => {
    switch (type) {
      case FacilityType.HOSPITAL:
        return "🏥";
      case FacilityType.CLINIC:
        return "🏥";
      case FacilityType.NURSING_HOME:
        return "🏠";
      case FacilityType.RADIOLOGY_CENTER:
        return "🩻";
      case FacilityType.HEALTH_CENTRE:
        return "🏥";
      case FacilityType.EMERGENCY_SERVICES:
        return "🚑";
      case FacilityType.PHYSIOTHERAPY_CENTER:
        return "🦽";
      case FacilityType.HEALTH_POST:
        return "🏥";
      default:
        return "🏢";
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-neutral-600">{t("common.loading")}</p>
        </div>
      </div>
    );
  }

  // Error state — a load failure only. MED-1546: `error` is the whole slice's
  // error, so a failed save (the edit modal already reports it with a toast)
  // used to replace the facility that is still perfectly loaded with this page.
  if (error && !facility) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/facilities")}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t("common.back")}</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                {t("admin.facilities.error")}
              </h1>
              <p className="text-neutral-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Facility not found
  if (!facility) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate("/admin/facilities")}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>{t("common.back")}</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">
                {t("admin.facilities.notFound")}
              </h1>
              <p className="text-neutral-600">
                {t("admin.facilities.notFoundDescription")}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="flex flex-wrap gap-3 justify-between items-center page-header">
        <div className="flex items-center space-x-3 min-w-0">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/facilities")}
            className="flex flex-shrink-0 items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">{t("common.back")}</span>
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl font-bold truncate sm:text-2xl text-neutral-900">
              {facility.name}
            </h1>
            <p className="hidden text-sm text-neutral-600 sm:block">
              {t("admin.facilities.detailSubtitle")}
            </p>
          </div>
        </div>
        <Button
          onClick={() => setShowEditModal(true)}
          className="flex flex-shrink-0 items-center space-x-2"
        >
          <Edit className="w-4 h-4" />
          <span>{t("common.edit")}</span>
        </Button>
      </div>

      {/* Two-column layout: facility content + manager sidebar */}
      <div className="flex flex-col gap-6 items-start lg:flex-row page-section">
        {/* ── Left column ─────────────────────────────────────────────────── */}
        <div className="flex-1 space-y-6 w-full min-w-0">
          {/* Facility Overview */}
          <div className="overflow-hidden card-container">
            <div className="flex flex-col sm:flex-row">
              {/* Facility Details */}
              <div className="flex-1 p-6">
                <div className="flex flex-wrap gap-3 justify-between items-start mb-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap gap-2 items-center mb-2">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getFacilityTypeColor(
                          facility.facilityType
                        )}`}
                      >
                        <span className="mr-2">
                          {getFacilityTypeIcon(facility.facilityType)}
                        </span>
                        {facilityTypeLabel(t, facility.facilityType)}
                      </span>
                      <StatusBadge
                        status={t(
                          facility.disabled
                            ? "common.statuses.inactive"
                            : "common.statuses.active"
                        )}
                      />
                      {facility.isPartner && (
                        <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full text-warning-800 bg-warning-100">
                          {t("admin.facilities.partner.partner")}
                        </span>
                      )}
                    </div>
                    <h2 className="mb-1 text-xl font-bold text-neutral-900">
                      {facility.name}
                    </h2>
                    {facility.contactEmail && (
                      <p className="text-sm text-neutral-500">
                        {facility.contactEmail}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={handleToggleStatus}
                    className="flex flex-shrink-0 items-center px-3 py-1.5 space-x-2 rounded-lg border border-neutral-300 hover:bg-neutral-50"
                    title={
                      facility.disabled
                        ? t("common.enable")
                        : t("common.disable")
                    }
                  >
                    {facility.disabled ? (
                      <ToggleLeft className="w-5 h-5 text-neutral-400" />
                    ) : (
                      <ToggleRight className="w-5 h-5 text-success-500" />
                    )}
                    <span className="text-sm font-medium">
                      {facility.disabled
                        ? t("common.enable")
                        : t("common.disable")}
                    </span>
                  </button>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 gap-3 mb-5 md:grid-cols-2">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="flex-shrink-0 mr-2 w-4 h-4" />
                    <div className="text-sm">
                      <div className="font-medium">
                        {facility.location.address}
                      </div>
                      <div className="text-neutral-500">
                        {facility.location.city}, {facility.location.state}{" "}
                        {facility.location.zip}
                      </div>
                    </div>
                  </div>
                  {facility.location.accessInformation && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Info className="flex-shrink-0 mr-2 w-4 h-4" />
                      <span>{facility.location.accessInformation}</span>
                    </div>
                  )}
                  <div className="flex items-center text-sm text-gray-600">
                    <Phone className="flex-shrink-0 mr-2 w-4 h-4" />
                    <span>
                      {formatPhoneNumberForDisplay(facility.phoneNumber)}
                    </span>
                  </div>
                  {facility.website && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Globe className="flex-shrink-0 mr-2 w-4 h-4" />
                      <a
                        href={facility.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate text-primary-600 hover:text-primary-800"
                      >
                        {facility.website}
                      </a>
                    </div>
                  )}
                  {(facility.rating != null || facility.reviews) && (
                    <div className="flex items-center text-sm text-gray-600">
                      <Star className="mr-2 w-4 h-4 text-yellow-400 fill-current" />
                      <span className="font-medium">{facility.rating}</span>
                      {facility.reviews ? (
                        <span className="ml-1 text-neutral-500">
                          (
                          {t("admin.facilities.reviewCount", {
                            count: facility.reviews,
                          })}
                          )
                        </span>
                      ) : null}
                    </div>
                  )}
                </div>

                {/* Services */}
                {facility.careTypes && facility.careTypes.length > 0 && (
                  <div className="mb-4">
                    <h3 className="mb-2 text-sm font-semibold text-gray-900">
                      {t("admin.facilities.services")}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {facility.careTypes.map((service, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 text-xs text-gray-700 bg-gray-100 rounded-full"
                        >
                          {service.label?.trim() || service.code}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Specialties */}
                {facility.specialties && facility.specialties.length > 0 && (
                  <div className="mb-4">
                    <h3 className="mb-2 text-sm font-semibold text-gray-900">
                      {t("admin.facilities.specialties")}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {facility.specialties.map((specialty, index) => (
                        <Tag
                          key={index}
                          label={resolveSpecialtyLabel(specialty)}
                          variant="primary"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Insurance */}
                {facility.insurance && facility.insurance.length > 0 && (
                  <div>
                    <h3 className="mb-2 text-sm font-semibold text-gray-900">
                      {t("admin.facilities.insurance")}
                    </h3>
                    <div className="flex flex-wrap gap-1.5">
                      {facility.insurance.map((insurance, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 text-xs text-green-700 bg-green-100 rounded-full"
                        >
                          {insurance}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Licence */}
          <FacilityLicenceSection facilityId={Number(facilityId)} />

          {/* Operating Hours */}
          <div className="card-container">
            <h3 className="mb-4 text-base font-semibold text-gray-900">
              {t("admin.facilities.operatingHours")}
            </h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {facility.hours &&
                Object.entries(facility.hours).map(([day, hours]) => (
                  <div key={day} className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 capitalize">
                      {weekdayLabel(t, day)}
                    </span>
                    <span className="text-sm text-gray-600">
                      {hours
                        ? `${hours.open} - ${hours.close}`
                        : t("common.closed")}
                    </span>
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* ── Right column: Manager card ───────────────────────────────────── */}
        <div className="w-full lg:flex-shrink-0 lg:w-72">
          <div className="overflow-hidden card-container manager-card-enter">
            {/* Card header */}
            <div className="px-5 py-4 border-b border-neutral-100">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-neutral-700">
                {t("admin.facilities.manager")}
              </h3>
            </div>

            {facilityManager ? (
              <div className="p-5 space-y-4 lg:space-y-4">
                {/* Avatar + name */}
                <div className="flex gap-4 items-center manager-row-1">
                  <div className="flex-shrink-0">
                    {facilityManager.photoUrl ? (
                      <img
                        src={facilityManager.photoUrl}
                        alt={`${facilityManager.firstName} ${facilityManager.lastName}`}
                        className="object-cover w-14 h-14 rounded-full ring-2 ring-primary-100 manager-avatar-pop manager-avatar-ring"
                      />
                    ) : (
                      <div className="flex justify-center items-center w-14 h-14 bg-gradient-to-br rounded-full ring-2 from-primary-100 to-primary-200 ring-primary-100 manager-avatar-pop manager-avatar-ring">
                        <span className="text-lg font-bold text-primary-600">
                          {facilityManager.firstName?.[0]}
                          {facilityManager.lastName?.[0]}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate text-neutral-900">
                      {civilityLabel(t, facilityManager.civility)}{" "}
                      {facilityManager.firstName} {facilityManager.lastName}
                    </p>
                    <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-medium bg-primary-50 text-primary-700">
                      {t("admin.roles.manager")}
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-neutral-100 manager-row-2" />

                {/* Contact details */}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
                  <div className="flex gap-3 items-center text-sm text-neutral-600 manager-row-3">
                    <div className="flex flex-shrink-0 justify-center items-center w-7 h-7 rounded-full bg-neutral-100">
                      <Mail className="w-3.5 h-3.5 text-neutral-500" />
                    </div>
                    <span className="truncate">{facilityManager.email}</span>
                  </div>
                  <div className="flex gap-3 items-center text-sm text-neutral-600 manager-row-4">
                    <div className="flex flex-shrink-0 justify-center items-center w-7 h-7 rounded-full bg-neutral-100">
                      <Phone className="w-3.5 h-3.5 text-neutral-500" />
                    </div>
                    <span>
                      {formatPhoneNumberForDisplay(facilityManager.phoneNumber)}
                    </span>
                  </div>
                </div>

                {/* Divider */}
                <div className="border-t border-neutral-100 manager-row-4" />

                {/* View profile link */}
                <div className="manager-row-5">
                  <Button
                    variant="outline"
                    className="w-full text-sm"
                    onClick={() =>
                      navigate(`/admin/managers/${facilityManager.id}`)
                    }
                  >
                    {t("admin.facilities.viewManager")}
                  </Button>
                </div>
              </div>
            ) : (
              /* No manager assigned */
              <div className="flex flex-col items-center p-5 space-y-4 text-center">
                <div className="flex justify-center items-center w-14 h-14 rounded-full bg-neutral-100 manager-avatar-pop">
                  <User className="w-7 h-7 text-neutral-400" />
                </div>
                <div className="manager-row-2">
                  <p className="text-sm font-medium text-neutral-700">
                    {t("admin.facilities.noManager")}
                  </p>
                  <p className="mt-1 text-xs text-neutral-500">
                    {t("admin.facilities.noManagerDescription")}
                  </p>
                </div>
                <div className="w-full manager-row-3">
                  <Button
                    className="w-full text-sm"
                    onClick={() => setShowAssignModal(true)}
                  >
                    <UserPlus className="mr-2 w-4 h-4" />
                    {t("admin.facilities.assignManager")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditFacilityModal
          isOpen={showEditModal}
          onClose={() => setShowEditModal(false)}
          facility={facility}
          onSuccess={handleEditFacilitySuccess}
        />
      )}

      <AssignManagerModal
        isOpen={showAssignModal}
        onClose={() => setShowAssignModal(false)}
        facilityId={Number(facilityId)}
        facilityName={facility.name}
        onSuccess={handleAssignManagerSuccess}
      />

      {showFeaturesModal && (
        <Modal
          isOpen={showFeaturesModal}
          onClose={() => setShowFeaturesModal(false)}
          title={t("admin.facilities.features")}
        >
          <div className="p-6">
            <p className="text-gray-600">
              {t("admin.facilities.featuresContent", {
                name: facility.name,
              })}
            </p>
            <div className="flex justify-end mt-6 space-x-3">
              <Button
                variant="outline"
                onClick={() => setShowFeaturesModal(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button onClick={() => setShowFeaturesModal(false)}>
                {t("common.save")}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Toggle Status Confirmation Modal */}
      {showToggleStatusModal && facility && (
        <ConfirmationModal
          isOpen={showToggleStatusModal}
          onClose={handleToggleStatusCancel}
          onConfirm={handleToggleStatusConfirm}
          title={
            facility.disabled
              ? t("common.confirmations.enableFacility")
              : t("common.confirmations.disableFacility")
          }
          message={
            facility.disabled
              ? t("common.confirmations.enableFacilityMessage")
              : t("common.confirmations.disableFacilityMessage")
          }
          confirmLabel={
            facility.disabled ? t("common.enable") : t("common.disable")
          }
          cancelLabel={t("common.cancel")}
          variant={facility.disabled ? "success" : "warning"}
          isLoading={togglingStatus}
        />
      )}
    </div>
  );
};
